<mat-sidenav-container [hasBackdrop]="responsive.isHandset()">
  <!-- SideNav: an always-visible rail on tablet/desktop (mode="side"); a slide-over drawer with a
       dismissable backdrop on phones (mode="over"), rendered fully expanded so labels show. -->
  <mat-sidenav
    [ngClass]="!responsive.isHandset() && !isSidenavOpen ? 'closed-mat-sidenav' : 'opened-mat-sidenav'"
    [mode]="responsive.isHandset() ? 'over' : 'side'"
    [opened]="responsive.isHandset() ? isSidenavOpen : true"
    (openedChange)="onSidenavOpenedChange($event)"
    fixedInViewport="true"
  >
    <app-side-nav [isSidenavOpen]="responsive.isHandset() ? true : isSidenavOpen" (sidenavToggle)="toggleSidenav()"></app-side-nav>
  </mat-sidenav>

  <!-- Right section: Toolbar + Page Content -->
  <mat-sidenav-content class="main-container" [ngClass]="{ 'sidenav-open': isSidenavOpen }">
    <!-- Toolbar -->
    <mat-toolbar
      class="custom-horizontal-toolbar display-inline-space-between"
      [ngClass]="{ 'width-handset': responsive.isHandset(), 'width-desktop': !responsive.isHandset() }"
    >
      <div class="display-inline-start">
        <!-- Hamburger: phones only. The sidenav is an overlay there and starts closed, so without a
             toolbar trigger it can't be opened. On tablet/desktop the rail is always visible and is
             toggled from within the sidenav itself, so no toolbar trigger is needed there. -->
        @if (responsive.isHandset()) {
          <button
            aria-label="Toggle navigation menu"
            type="button"
            class="menu-toggle-button"
            [disabled]="passwordExpiryService.hasPasswordExpired()"
            (click)="toggleSidenav()"
            mat-icon-button
          >
            <mat-icon>menu</mat-icon>
          </button>
        }
        <div
          tabindex="0"
          role="button"
          class="application-container"
          [attr.aria-label]="'Go to ' + applicationTitle + ' home'"
          (click)="redirectToHomePage()"
          (keydown.enter)="redirectToHomePage()"
          (keydown.space)="redirectToHomePage()"
        >
          <span class="application-logo-chip">
            <img alt="Logo" class="application-logo" [src]="toolbarLogoUrl" />
          </span>
          <span class="application-title">{{ applicationTitle }}</span>
        </div>
      </div>

      @if (!responsive.isHandset()) {
        <div class="login-entity-name">{{ userInfo?.kiClient?.name }}</div>
      }

      <div class="display-inline-end">
        <!-- Light/Dark theme toggle - hidden for now (September 2026); ThemeService.DARK_MODE_ENABLED -->
        @if (darkModeEnabled) {
          <button
            type="button"
            class="theme-toggle-button"
            [matTooltip]="(isDarkMode ? 'COMMON.SWITCH_TO_LIGHT_MODE' : 'COMMON.SWITCH_TO_DARK_MODE') | translate"
            (click)="toggleDarkMode()"
            mat-icon-button
          >
            <mat-icon>{{ isDarkMode ? 'light_mode' : 'dark_mode' }}</mat-icon>
          </button>
        }

        <!-- Notification -->
        <button
          type="button"
          class="notification-button"
          [disabled]="passwordExpiryService.hasPasswordExpired()"
          [routerLink]="['/notifications']"
          mat-icon-button
          matTooltip="{{ 'COMMON.NOTIFICATIONS' | translate }}"
        >
          <mat-icon class="notification-icon">notifications</mat-icon>
          @if (unreadNotificationCount > 0) {
            <span class="notification-count">{{ notificationBadgeLabel }}</span>
          }
        </button>

        <!-- Language Selector -->
        <button aria-label="Select language" type="button" class="toolbar-menu-button language-button" [matMenuTriggerFor]="langMenu" mat-button>
          <mat-icon class="language-icon">translate</mat-icon>
          @if (!responsive.isHandset()) {
            <span class="language-label">{{ getCurrentLanguageLabel() }}</span>
          }
          @if (!responsive.isHandset()) {
            <mat-icon class="dropdown-icon">arrow_drop_down</mat-icon>
          }
        </button>
        <mat-menu #langMenu="matMenu" class="language-menu">
          @for (language of languages(); track language.id) {
            <button
              type="button"
              class="language-menu-item"
              [ngClass]="{ 'selected-lang': languageControl.value === language.code }"
              (click)="languageControl.setValue(language.code!)"
              mat-menu-item
            >
              <span class="language-item-label">
                {{ language.name }}
                @if (languageControl.value === language.code) {
                  <mat-icon>check</mat-icon>
                }
              </span>
            </button>
          }
        </mat-menu>

        <!-- User menu -->
        <button type="button" class="toolbar-menu-button user-button" [matMenuTriggerFor]="menu" mat-button>
          @if (userInfo?.profilePic) {
            <img alt="Profile" class="profile-pic" [src]="userInfo?.profilePic" />
          } @else {
            <mat-icon class="profile-icon">account_circle</mat-icon>
          }
          @if (!responsive.isHandset()) {
            <span class="username"
              >{{ userName }}
              <mat-icon class="dropdown-icon">arrow_drop_down</mat-icon>
            </span>
          } @else {
            <span class="username">
              <mat-icon class="dropdown-icon">arrow_drop_down</mat-icon>
            </span>
          }
        </button>
        <mat-menu #menu="matMenu">
          @if (!isApplicationSupport) {
            <button type="button" (click)="changePassword()" mat-menu-item>
              <mat-icon>password</mat-icon>
              <span>{{ 'COMMON.CHANGE_PASSWORD' | translate }}</span>
            </button>
          }
          <button type="button" (click)="onLogout()" mat-menu-item>
            <mat-icon>logout</mat-icon>
            <span>{{ 'COMMON.LOGOUT' | translate }}</span>
          </button>
        </mat-menu>
      </div>
    </mat-toolbar>

    <!-- Page Content -->
    <div class="page-content">
      <!-- <app-breadcrumb></app-breadcrumb> -->
      <router-outlet></router-outlet>
    </div>

    <div>
      <mat-toolbar class="custom-footer">
        <div class="featured-by-container">
          <div class="marquee-content">
            {{ 'COMMON.POWERED_BY_PREFIX' | translate }}
            <!-- <span class="codatron-text">{{ 'COMMON.CODATRON' | translate }}</span> -->
            <span
              tabindex="0"
              class="codatron-text"
              (click)="redirectToCodatron()"
              (keydown.enter)="redirectToCodatron()"
              (keydown.space)="redirectToCodatron()"
              >{{ 'COMMON.CODATRON' | translate }}</span
            >
            {{ 'COMMON.POWERED_BY_SUFFIX' | translate }}
            <img
              alt="Logo"
              class="codatron-logo"
              [src]="codatronLogoUrl"
              (click)="redirectToCodatron()"
              (keydown.enter)="redirectToCodatron()"
              (keydown.space)="redirectToCodatron()"
            />
          </div>
        </div>
      </mat-toolbar>
    </div>
  </mat-sidenav-content>
</mat-sidenav-container>
